import { useCallback, useMemo } from 'react';
import { useKstToday } from '@/shared/lib';
import { useSearchParams } from 'react-router';

/**
 * URL 쿼리스트링을 SSOT 로 쓰는 필터 상태의 **도메인 무관 코어** — 새로고침·공유·뒤로가기 안전.
 *
 * 화면이 제공하는 것은 코덱(`decode`/`encode`)·기본값·동치 판정 셋뿐이고, 여기서는 그것들을
 * `searchParams` 에 바인딩하는 배선만 한다. 도메인 어휘(등급 CSV·인버터 목록·기간 프리셋)는 전부
 * feature 의 코덱에 남는다 — 이 파일이 도메인을 알기 시작하면 화면마다 분기가 생긴다.
 *
 * 코덱 계약(**두 방향 모두 지켜야 이 훅이 성립한다**):
 *  - `decode` 는 **항상 유효한 필터**를 돌려준다. 누락·불량·의미 위반(역전 기간 등)은 기본값으로 격하해
 *    호출처가 `undefined` 를 몰라도 되게 한다(진입 방어의 위치를 여기 한 곳으로 고정).
 *  - `encode` 는 **기본값을 생략**한다. 기본 상태의 URL 이 깨끗해야 공유 링크에 의도한 조건만 남고,
 *    "오늘" 처럼 매 로드 재계산돼야 하는 기본값이 URL 에 굳지 않는다.
 */
export interface UrlFilterCodec<T> {
  /** 쿼리스트링 → 필터. 불량 입력은 기본값으로 격하(throw 금지). */
  decode: (search: string) => T;
  /** 필터 → 쿼리스트링. 기본값은 생략. */
  encode: (filters: T) => URLSearchParams;
}

export interface UrlFiltersOptions {
  /**
   * 히스토리 대신 **현재 항목을 치환**할지(기본 false = push).
   *
   * **live 필터(즉시 반영)는 `true`** — 날짜 한 칸·토글 하나마다 항목이 쌓이면 뒤로가기가 "화면 나가기" 가
   * 아니라 "필터 되감기" 가 된다. **staged 필터([조회] 버튼)는 `false`** — 한 번의 조회가 곧 하나의 의미 있는
   * 단계라 되돌릴 수 있는 편이 낫다. 어느 쪽이든 공유·새로고침·북마크는 동일하게 동작한다.
   */
  replace?: boolean;
}

export interface UrlFilterState<T> {
  /** 적용된 필터(SSOT = URL) — 쿼리·칩·집계 소스. */
  filters: T;
  /** Applied≠기본값 — [초기화] 노출 판정. */
  isDirty: boolean;
  /** 필터 통째 교체(칩 제거·[조회] 등). */
  apply: (next: T) => void;
  /** 전체 초기화 — 조회조건 쿼리스트링을 비운다. */
  reset: () => void;
  /**
   * 부분 갱신 seam — 화면별 세터(기간 cascade·인버터 선택 등)가 `(prev) => next` 로 확장할 때.
   * 항상 **현재 URL 을 다시 디코드**해 얹으므로 연속 변경이 서로를 덮어쓰지 않는다.
   */
  write: (updater: (prev: T) => T) => void;
}

/**
 * URL 필터 상태 훅 — `codec` 으로 searchParams 를 읽고 쓴다.
 *
 * `makeDefault`·`equal` 은 **모듈 레벨 안정 함수**를 넘길 것(렌더마다 새 함수를 만들면 메모이즈가 깨진다).
 */
export function useUrlFilters<T>(
  codec: UrlFilterCodec<T>,
  makeDefault: () => T,
  equal: (a: T, b: T) => boolean,
  options: UrlFiltersOptions = {},
): UrlFilterState<T> {
  const { replace = false } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  // 문자열로 메모이즈 — searchParams 객체 동일성에 의존하면 매 렌더 새 필터 → 무한 refetch.
  const search = searchParams.toString();
  const { decode, encode } = codec;
  // `today` — 코덱이 누락 날짜를 '오늘' 기본값으로 채우므로, 날짜 경계를 넘으면 같은 URL 이라도 다시 decode 해야
  // 한다(안 그러면 자정 넘긴 세션이 어제 기본값에 굳는다). 명시 날짜(URL 에 있는 값)는 decode 결과가 그대로라 무영향.
  const today = useKstToday();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- today 는 decode 내부 getKstToday() 재계산 트리거(본문 미참조)
  const filters = useMemo(() => decode(search), [decode, search, today]);
  const isDirty = useMemo(() => !equal(filters, makeDefault()), [filters, equal, makeDefault]);

  const write = useCallback(
    (updater: (prev: T) => T) => {
      // functional 형태 — React 배치 중에도 직전 결과 위에 쌓인다(stale closure 로 앞의 변경이 씹히지 않게).
      setSearchParams((prev) => encode(updater(decode(prev.toString()))), { replace });
    },
    [setSearchParams, decode, encode, replace],
  );

  const apply = useCallback((next: T) => write(() => next), [write]);
  const reset = useCallback(() => write(makeDefault), [write, makeDefault]);

  return { filters, isDirty, apply, reset, write };
}
